// Shared navigation

import {
  NavigationItem,
  NavigationItemList,
  NavigationItemWithSublinks,
} from '@components/Navigation/types';
import {
  InboxNavIcon,
  NoticeboardIcon,
  StockIcon,
  ShipmentsIcon,
  IntraUnitSupplyIcon,
  SkusIcon,
  CustomersIcon,
  SuppliersIcon,
  InvoicesIcon,
  PaymentsIcon,
  WarehouseShortlistIcon,
  FindWarehouseIcon,
  WarehousesIcon,
  FleetVehiclesIcon,
  TransportTariffsIcon,
  DepotsIcon,
  DashboardIcon,
} from '@components/Navigation/icons';

// Top-level items (no section header)
const inboxNavigation: NavigationItem = {
  id: 'inbox',
  label: 'Inbox',
  href: '#', // Opens inbox panel
  icon: InboxNavIcon,
};

const noticeboardNavigation: NavigationItem = {
  id: 'noticeboard',
  label: 'Noticeboard',
  href: '/noticeboard',
  icon: NoticeboardIcon,
};

const financeNavigation: NavigationItemWithSublinks = {
  id: 'finance',
  label: 'Finance',
  href: '#',
  sublinks: [
    {
      id: 'invoice',
      label: 'Invoices',
      href: '/invoices',
      icon: InvoicesIcon,
      activePatterns: ['/invoice/'],
    },
    {
      id: 'payments',
      label: 'Payments',
      href: '/payments',
      icon: PaymentsIcon,
      activePatterns: ['/payment/'],
    },
  ],
};

// Warehouse Client navigation

const clientOperationsNavigation: NavigationItemWithSublinks = {
  id: 'operations',
  label: 'Operations',
  href: '#',
  sublinks: [
    {
      id: 'stock',
      label: 'Stock',
      href: '/stock',
      icon: StockIcon,
    },
    {
      id: 'shipments',
      label: 'Shipments',
      href: '/shipments',
      icon: ShipmentsIcon,
      activePatterns: [
        '/inbound-shipment/',
        '/despatch-note/',
        '/outbound-shipment/',
      ],
    },
  ],
};

const clientMasterDataNavigation: NavigationItemWithSublinks = {
  id: 'masterData',
  label: 'Master Data',
  href: '#',
  sublinks: [
    {
      id: 'skuItemsMaster',
      label: 'Products',
      href: '/sku-master',
      icon: SkusIcon,
      description: 'Create, view and edit your products and their SKUs.',
      activePatterns: ['/sku/'],
    },
    {
      id: 'customerMaster',
      label: 'Customers',
      href: '/customer-master',
      icon: CustomersIcon,
      description: 'View and edit your customers.',
    },
    {
      id: 'supplierMaster',
      label: 'Suppliers',
      href: '/supplier-master',
      icon: SuppliersIcon,
      description: 'View and edit your suppliers.',
    },
  ],
};

const clientWarehouseSearchNavigation: NavigationItemWithSublinks = {
  id: 'warehousing',
  label: 'Warehousing',
  href: '#',
  sublinks: [
    {
      id: 'warehouseShortlist',
      label: 'Warehouses Shortlist',
      href: '/warehouse-shortlist',
      icon: WarehouseShortlistIcon,
      description: 'View and edit your warehouse shortlist.',
    },
    {
      id: 'warehouseSearch',
      label: 'Find a Warehouse',
      href: '/warehouse-search',
      icon: FindWarehouseIcon,
      description: 'Search for warehouses.',
      activePatterns: ['/warehouse/'],
    },
    {
      id: 'transportOrder',
      label: 'Transport Order',
      href: '/transport-order/create',
      icon: IntraUnitSupplyIcon,
    },
  ],
};

// Warehouse Manager navigation

const managerOperationsNavigation: NavigationItemWithSublinks = {
  id: 'operations',
  label: 'Operations',
  href: '#',
  sublinks: [
    {
      id: 'customers',
      label: 'Customers',
      href: '/customers',
      icon: CustomersIcon,
    },
    {
      id: 'stock',
      label: 'Stock',
      href: '/stock',
      icon: StockIcon,
    },
    {
      id: 'shipments',
      label: 'Shipments',
      href: '/shipments',
      icon: ShipmentsIcon,
      activePatterns: [
        '/inbound-shipment/',
        '/despatch-note/',
        '/outbound-shipment/',
      ],
    },
  ],
};

const warehouseMasterNavigation: NavigationItemWithSublinks = {
  id: 'masterData',
  label: 'Master Data',
  href: '#',
  sublinks: [
    {
      id: 'warehouseMaster',
      label: 'Warehouses/Depots',
      href: '/warehouse-master',
      icon: WarehousesIcon,
      description: 'View and edit warehouses and depots.',
      activePatterns: ['/warehouse/', '/depot/'],
    },
    {
      id: 'fleetMaster',
      label: 'Fleet Vehicles / Rates',
      href: '/fleet-master',
      icon: FleetVehiclesIcon,
      description: 'View and edit your fleet and set your price per mile.',
      activePatterns: ['/vehicle/'],
    },
    {
      id: 'priceMaster',
      label: 'Network Tariffs',
      href: '/transport-price-master',
      icon: TransportTariffsIcon,
      description: 'View and edit your network rate cards.',
      activePatterns: ['/network-ratecard/'],
    },
  ],
};

// Prepended by RequireAuth behind the aiFeatures flag (Wingman runs on stage only for now).
// The route stays /wingman-board — /wingman itself belongs to the gateway proxy.
export const dashboardNavigation: NavigationItem = {
  id: 'dashboard',
  label: 'Dashboard',
  href: '/wingman-board',
  icon: DashboardIcon,
};

export const clientNavigation: NavigationItemList = [
  inboxNavigation,
  noticeboardNavigation,
  clientOperationsNavigation,
  clientMasterDataNavigation,
  financeNavigation,
  clientWarehouseSearchNavigation,
];

export const managerNavigation: NavigationItemList = [
  inboxNavigation,
  noticeboardNavigation,
  managerOperationsNavigation,
  warehouseMasterNavigation,
  financeNavigation,
];
